<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';

import StatBar from './components/StatBar.vue'
import OptionSelector from './components/OptionSelector.vue'
import StatsTextGrid from './components/StatsTextGrid.vue'
import DaysOfWeekChart from './components/DaysOfWeekChart.vue'
import RepsOverTimeLineChart from './components/RepsOverTimeLineChart.vue'
import CalendarHeatmap from './components/CalendarHeatmap.vue'
import TimesOfDayChart from './components/TimesOfDayChart.vue'

import JSConfetti from 'js-confetti';

import { exerciseDataNameToLabelRemapper } from './exercise-helpers.js';

import workoutDataJohn2023 from './assets/workout-data-john-2023.json';
import workoutDataKatie2023 from './assets/workout-data-katie-2023.json';

import workoutDataJohn2024 from './assets/workout-data-john-2024.json';
import workoutDataKatie2024 from './assets/workout-data-katie-2024.json';
import workoutDataMatt2024 from './assets/workout-data-matt-2024.json';

import workoutDataJohn2025 from './assets/workout-data-john-2025.json';
import workoutDataKatie2025 from './assets/workout-data-katie-2025.json';
import workoutDataMatt2025 from './assets/workout-data-matt-2025.json';

const johnData = { '2023': workoutDataJohn2023, '2024': workoutDataJohn2024, '2025': workoutDataJohn2025 };
const katieData = { '2023': workoutDataKatie2023, '2024': workoutDataKatie2024, '2025': workoutDataKatie2025 };
const mattData = { '2024': workoutDataMatt2024, '2025': workoutDataMatt2025 };




// Dynamically import, doesnt work tree shaking as they are required in this file
// const workoutDataJohn = () => import('./assets/workout-data-john-2024.json');
// const workoutDataKatie = () => import('./assets/workout-data-katie-2024.json');
// const workoutDataMatt = () => import('./assets/workout-data-matt-2024.json');

import RepsPerDayChart from './components/RepsPerDayChart.vue';

const showChallengeYear = ref(false);

const selectedYear = ref('2024');

const selectedPerson = ref('john');
const selectedPersonData = ref(null);
const availableExercises = ref(null);
const selectedExercise = ref(null);

(function parseUrlParams() {
	const urlParams = new URLSearchParams(window.location.search);
	const person = urlParams.get('person');
	const exercise = urlParams.get('exercise');
	const year = urlParams.get('year');

	if (person) {
		changeSelectedPersonData(person);
	}

	if (exercise) {
		selectedExercise.value = exercise;
	}

	if (year) {
		selectedYear.value = year;
	}


})();
// parseUrlParams();

changeSelectedPersonData(selectedPerson.value);

function changeSelectedPersonData(person) {
	switch (person) {
		default:
		case 'john':
			selectedPersonData.value = johnData[selectedYear.value] ?? johnData['2024'];
			selectedPerson.value = 'john';
			break;
		case 'katie':
			selectedPersonData.value = katieData[selectedYear.value] ?? katieData['2024'];
			selectedPerson.value = 'katie';
			break;
		case 'matt':
			selectedPersonData.value = mattData[selectedYear.value] ?? mattData['2024'];
			selectedPerson.value = 'matt';
			break;
	}

	availableExercises.value = Object.keys(selectedPersonData.value);

	// If selected exercise is not inside availableExercises, set it to the first one
	if (!availableExercises.value.includes(selectedExercise.value)) {
		selectedExercise.value = availableExercises.value[0];
	}

	if (selectedPersonData.value == null) {
		selectedExercise.value = availableExercises.value[0];
	}
}

if (selectedPersonData == null) {
	throw new Error('No workout data found');
}

if (availableExercises.value == null || availableExercises.value.length === 0) {
	throw new Error('No exercises found in data');
}

const selectedWorkout = computed(() => {
	try {
		return selectedPersonData.value[selectedExercise.value];
	} catch (e) {		
		throw new Error('No workout data found for selected exercise');
	}
});

const isExerciseBehindSchedule = computed(() => {
	return (selectedWorkout.value.current.repsAheadOfGoal < 0);
});

const isGoalMet = computed(() => {
	return (selectedWorkout.value.current.targetMet == 'true');
});

const allChallengeGoalsMet = computed(() => {
	return Object.values(selectedPersonData.value).every((exercise) => exercise.current.targetMet == 'true');
});

const isActiveChallenge = computed(() => {
	return selectedWorkout.value.goal.isActiveChallenge || false
});

const dateOfStatsExport = computed(() => {
	return selectedWorkout.value.dateOfExport;
	// return dayjs(selectedWorkout.value.dateOfExport).format('Do MMMM YYYY');
	return new Date(selectedWorkout.value.dateOfExport).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
});

const challengeYear = computed(() => {
	return selectedWorkout.value.goal.challengeYear ?? 'Unknown';
});

const challengeGoal = computed(() => {
	return (selectedWorkout.value.goal.repCount / 1000) ?? 'Unknown';
});

const heatmapData = ref(selectedWorkout.value.dailyDataFullGrouped);

const confetti = new JSConfetti();

if (allChallengeGoalsMet.value) {
	confetti.addConfetti();
}

watch(selectedPerson, (person) => {
	changeSelectedPersonData(person);
	availableExercises.value = Object.keys(selectedPersonData.value);

	nextTick(() => {
		if (allChallengeGoalsMet.value) {
			confetti.addConfetti();
		}
	});
});

watch(selectedWorkout, () => {
	changeHeatmapData();
});

const changeHeatmapData = async () => {
    await scheduler.yield();
	heatmapData.value = selectedWorkout.value.dailyDataFullGrouped;
}

const progressEmoji = computed(() => {
	const percentageAgainstTarget = Math.abs(selectedWorkout.value.current.repsAheadOfGoal) / selectedWorkout.value.goal.repCount;

	if (isExerciseBehindSchedule.value) {
		switch (true) {
			case percentageAgainstTarget > 0.3:
				return '😭';
			case percentageAgainstTarget > 0.2:
				return '😢';
			case percentageAgainstTarget > 0.1:
				return '😟';
			case percentageAgainstTarget > 0.05:
				return '🤨';
			default:
				return '😐';
		}

	} else {
		switch (true) {
			case percentageAgainstTarget > 0.3:
				return '😍';
			case percentageAgainstTarget > 0.2:
				return '🤩';
			case percentageAgainstTarget > 0.1:
				return '😁';
			case percentageAgainstTarget > 0.05:
				return '😃';
			default:
				return '😀';
		}
	}
});
</script>

<template>
	<main>
		<OptionSelector type="person" label="Select a person" :selected-value='selectedPerson' :is-sticky="true" :options="[ 'john', 'katie', 'matt']" inputName="person" @selection-updated="(newValue) => { selectedPerson = newValue }"/>

		<section>
			<div class="container container--narrow">
				<header>
					<h1>{{ challengeGoal }}k Challenge</h1>
					<p class="text-center"><em>Stats as of {{dateOfStatsExport }}</em></p>
					<p class="text-center" v-if="showChallengeYear">Challenge Year: {{challengeYear }}</p>
				</header>
				<div class="statGroup">
					<StatBar v-for="exercise in availableExercises" :label="exerciseDataNameToLabelRemapper(exercise)" :value="selectedPersonData[exercise].current.runningCount" :maxValue="selectedPersonData[exercise].goal.repCount" />
				</div>

				<div ref="fullConfetti"></div>

				<div class="mb-4">
					<h2>Why</h2>
					<p>This website was built to easily display metrics across different exercises. It started with a challenge with my Fiance to try and improve our fitness and aim towards a shared goal; achieve 10k reps. Since then, we've expanded upon our goal, and added more exercises, which spawned the desire to create this site to showcase our progress, and help motivate us to keep the rep counts coming in.</p>
				</div>
			</div>
		</section>
		<OptionSelector :is-sticky="true" label="Select an exercise" :options="availableExercises" inputName="exercise" :selected-value="selectedExercise" @selection-updated="(newValue) => { selectedExercise = newValue }"/>
		<section>
			<div class="container container--narrow">
				<h2 class="mb-1">{{ exerciseDataNameToLabelRemapper(selectedExercise) }}</h2>
					
				<StatsTextGrid class="mb-2" :columns="2" :stats="[
					{ label: 'Total Completed', value: selectedWorkout.current.runningCount, animated: true, decimals: 0, suffix: ' reps'},
					{ label: 'Daily Average', value: selectedWorkout.current.avgPerDay.toFixed(1),  animated: true, decimals: 1, suffix: ' reps'},
					{ label: 'Days Missed', value: selectedWorkout.current.inactiveDays, animated: true, decimals: 0},
					{ label: 'Best Day', value: `${selectedWorkout.current.highestRepDay.repCount}, ${new Date(selectedWorkout.current.highestRepDay.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}` },
					{ label: 'Highest Active Streak', value: selectedWorkout.streaks.longestActiveByDaysCount,  animated: true, decimals: 0, suffix: ' days' },
					{ label: 'Highest Inactive Streak', value: selectedWorkout.streaks.longestInactiveByDaysCount, animated: true, decimals: 0, suffix: ' days'},
				]" />

				<div class="mb-4">
					<div class="progress-emoji">{{ progressEmoji }}</div>
					<template v-if="isActiveChallenge && isExerciseBehindSchedule">
						<p>Oof, not on track, need to step those numbers up! <span class="capitalize">{{ selectedPerson }}</span> is <strong>{{ Math.abs(selectedWorkout.current.repsAheadOfGoal).toFixed(1) }} reps</strong> behind, which equates to <strong>{{ Math.abs(selectedWorkout.current.daysAheadOfGoal).toFixed(1) }} days</strong> behind. Doing <strong>{{ selectedWorkout.current.avgRequiredPerDayToTarget.toFixed(1) }} reps</strong> per day will get us back on track. We have <strong>{{ selectedWorkout.goal.totalDays - selectedWorkout.current.totalDaysSoFar }} days</strong> left.</p> 
					</template>
					<template v-else-if="isActiveChallenge && isExerciseBehindSchedule == false">
						<p>Yes! We're on track - <span class="capitalize">{{ selectedPerson }}</span> is <strong>{{ Math.abs(selectedWorkout.current.repsAheadOfGoal).toFixed(1) }} </strong> reps ahead, which equates to <strong>{{ Math.abs(selectedWorkout.current.daysAheadOfGoal).toFixed(1) }}</strong> days. We only really need to do <strong>{{ selectedWorkout.current.avgRequiredPerDayToTarget.toFixed(1) }}</strong> reps per day to get us to the finish 🏁.</p> 
					</template>
					<template v-else-if="isActiveChallenge && isGoalMet">
						<p>Wow <span class="capitalize">{{ selectedPerson }}</span> completed their goal even before the deadline. They absolutely smashed it.</p> 
					</template>
					<template v-else-if="isActiveChallenge == false && isGoalMet">
							<p>Yes! Goal achieved, excellent work - <span class="capitalize">{{ selectedPerson }}</span> met the goal of <strong>{{ Math.abs(selectedWorkout.goal.repCount).toFixed(1) }} </strong>.</p> 
					</template>
					<template v-else-if="isActiveChallenge == false && isGoalMet == false">
						<p>Sadly, the challenge time ran out and <span class="capitalize">{{ selectedPerson }}</span> didn't met the goal of <strong>{{ Math.abs(selectedWorkout.goal.repCount).toFixed(1) }} </strong>. Perhaps next year they will</p> 
					</template>
					<template v-else>
						<p>Else statement hit, oh dear. This needs to be looked into by the dev </p>
					</template>
				</div>
			</div>
			
			<div class="container">
				<RepsOverTimeLineChart
					class="mb-4 pull-out-to-edge" 
					chartTitle="Reps over time"
					xAxisTitle="Time"
					yAxisTitle="Reps"
					:data="selectedWorkout.dailyData"
					:expectedAmountSoFar="selectedWorkout.goal.expectedRepCountSoFar"
					:targetAmount="selectedWorkout.goal.repCount"
					:totalDaysSoFar="selectedWorkout.current.totalDaysSoFar"
				/>

				<DaysOfWeekChart 
					class="mb-4 pull-out-to-edge"
					chartTitle="Average across days of week"
					xAxisTitle="Day"
					yAxisTitle="Reps"
					:data="selectedWorkout.daysOfWeek"
				/>

				<RepsPerDayChart 
					class="mb-4 pull-out-to-edge"
					chartTitle="Reps per day"
					xAxisTitle="Day"
					yAxisTitle="Reps"
					:data="selectedWorkout.dailyDataFull"
					:targetAmount="Math.ceil(selectedWorkout.goal.avgRequiredPerDay)"
				/>

				<CalendarHeatmap 
					class="mb-4 pull-out-to-edge"
					chartTitle="Yearly Review"
					xAxisTitle="Months"
					yAxisTitle="Days"
					:data="heatmapData"
				/>
				
				<TimesOfDayChart 
					class="mb-4 pull-out-to-edge"
					chartTitle="Percentage reps across hours of day"
					:data="selectedWorkout.current.reps.hoursOfTheDay"
					xAxisTitle="Hour"
					yAxisTitle="%"
				/>
			</div>
		</section>

		<section>
			<div class="container container--narrow">
				<h2>How its made</h2>
				<p>Exercises are tracked using third-party website <a href="https://logreps.com/" target="_blank">logreps.com</a>, which offers a CSV export of all the rep data. I then wrote a Python pandas script to parses the data, calculates statistics and exports to a JSON file that this website uses. This site uses VueJS 3, with ApexCharts for the graphing.</p>

				<p>💖 thanks for reading - John</p>
			</div>
		</section>
	</main>
</template>

<style lang="scss">
	.statGroup {
		display: flex;
		gap: 15px;
		flex-direction: column;
		margin-bottom: 3rem;
	}

	.pull-out-to-edge {
		@media (max-width: 767.9px) {
			margin-left: -1rem;
			margin-right: -1rem;
		}
	}

	.visually-hidden {
		width: 1px !important;
		height: 1px !important;
		padding: 0 !important;
		margin: -1px !important;
		overflow: hidden !important;
		clip: rect(0, 0, 0, 0) !important;
		white-space: nowrap !important;
		border: 0 !important;

		&:not(caption) {
			position: absolute !important;
		}
	}
</style>
